import React from "react";
import { CloseIcon } from "../../../../icons";
import { LoadingSpinner } from "../../../ui/LoadingSpinner";

// Common props, excluding those that differ based on category usage
interface BaseSidebarProps {
  title: string;
  onClose: () => void;
  onSave: () => Promise<void>;
  isUpdating: boolean;
  updateError: string | null;
  hideFooterError?: boolean;
  children: React.ReactNode;
}

// Props for when categories ARE used
interface SidebarWithCategoriesProps<
  TItem extends { category?: string | null },
> extends BaseSidebarProps {
  selectedItem: TItem | null;
  allCategories: string[]; // Must be provided
  onSelectedItemChange: (item: TItem) => void; // Must be provided
}

// Props for when categories are NOT used
interface SidebarWithoutCategoriesProps<TItem> extends BaseSidebarProps {
  selectedItem: TItem | null;
  allCategories?: never; // Explicitly not allowed or undefined
  onSelectedItemChange?: never; // Explicitly not allowed or undefined
}

// Overloaded function signatures
export function DataManagementSidebar<
  TItem extends { category?: string | null },
>(props: SidebarWithCategoriesProps<TItem>): React.ReactElement | null;
export function DataManagementSidebar<TItem>(
  props: SidebarWithoutCategoriesProps<TItem>,
): React.ReactElement | null;

// Implementation
export function DataManagementSidebar<TItem>(
  props:
    | SidebarWithCategoriesProps<TItem & { category?: string | null }>
    | SidebarWithoutCategoriesProps<TItem>,
): React.ReactElement | null {
  const {
    selectedItem,
    onClose,
    title,
    children,
    onSave,
    isUpdating,
    updateError,
    hideFooterError,
  } = props;

  // Type guard to determine which version of props we have
  const hasCategories =
    "allCategories" in props && props.allCategories !== undefined;

  return (
    <>
      {/* Backdrop: click outside sidebar (including header/app nav) to close */}
      {selectedItem !== null && (
        <div
          aria-hidden
          className="fixed inset-0 z-40 top-0 left-0 right-0 bottom-0"
          onClick={onClose}
        />
      )}
      {/* Sidebar Container - Always rendered, transform controls visibility */}
      <div
        className={`
          fixed right-0 top-[70px] w-[30%] h-[calc(100vh-70px)] 
          px-5 py-5 shadow-[0_0_15px_rgba(0,0,0,0.1)] dark:shadow-[0_0_15px_rgba(255,255,255,0.1)] 
          bg-white dark:bg-black overflow-y-auto overflow-x-visible z-50 
          transform transition-transform duration-300 ease-in-out
          ${selectedItem !== null ? "translate-x-0" : "translate-x-full"}
        `}
      >
        {/* Sidebar Content - Conditionally rendered */}
        {selectedItem !== null && (
          <>
            {/* Close Button */}
            <div className="flex justify-end mb-2">
              <button
                className="text-black dark:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full p-2 transition-colors"
                onClick={onClose}
              >
                <CloseIcon className="w-6 h-6" />
              </button>
            </div>

            {/* Title */}
            <h2 className="text-xl font-semibold mb-4">{title}</h2>

            {/* Form Content */}
            <div className="space-y-4">
              {/* Category Dropdown - Conditionally render */}
              {hasCategories &&
                (
                  props as SidebarWithCategoriesProps<
                    TItem & { category?: string | null }
                  >
                ).allCategories.length > 0 &&
                selectedItem && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Category
                    </label>
                    <select
                      value={
                        (selectedItem as TItem & { category?: string | null })
                          .category || ""
                      }
                      onChange={(e) => {
                        // props is SidebarWithCategoriesProps<TItem>, so onSelectedItemChange is defined and TItem has category
                        const categoryProps =
                          props as SidebarWithCategoriesProps<
                            TItem & { category?: string | null }
                          >;
                        const currentItem = selectedItem as TItem & {
                          category?: string | null;
                        };
                        categoryProps.onSelectedItemChange({
                          ...currentItem,
                          category: e.target.value || null,
                        });
                      }}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      {(
                        props as SidebarWithCategoriesProps<
                          TItem & { category?: string | null }
                        >
                      ).allCategories.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              {children}
            </div>

            {/* Save and Cancel Buttons */}
            <div className="flex items-center justify-end gap-4 mt-6">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={onSave}
                disabled={isUpdating}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdating ? (
                  <div className="flex items-center gap-2">
                    <LoadingSpinner size="sm" color="white" />
                    <span>Saving...</span>
                  </div>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>

            {updateError && !hideFooterError && (
              <p className="text-red-500 text-sm mt-2">{updateError}</p>
            )}
          </>
        )}
      </div>
    </>
  );
}
